import React from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowDownLeft, ArrowUpRight, RotateCcw, Settings2, Shuffle,
  TrendingUp, Package, DollarSign, Skull,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";

function fmtNum(n) {
  if (n === null || n === undefined) return "—";
  return Math.abs(n) >= 1000
    ? Number(n).toLocaleString("en-US")
    : Number(n).toString();
}

function fmtMoney(n) {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

const KPI_DEFS = [
  { key: "entradas",       icon: ArrowDownLeft, accent: "text-emerald-600", source: "movement", qtyKey: "total_qty" },
  { key: "salidas",        icon: ArrowUpRight,  accent: "text-rose-600",    source: "movement", qtyKey: "total_qty" },
  { key: "devoluciones",   icon: RotateCcw,     accent: "text-amber-600",   source: "movement", qtyKey: "total_qty" },
  { key: "ajustes",        icon: Settings2,     accent: "text-violet-600",  source: "movement", qtyKey: "total_qty" },
  { key: "transferencias", icon: Shuffle,       accent: "text-sky-600",     source: "movement", qtyKey: "total_qty" },
  { key: "delta_stock",    icon: TrendingUp,    accent: "text-primary",     source: "scalar",   field: "delta_stock_in_range", isQty: true },
  { key: "current_stock",  icon: Package,       accent: "text-foreground",  source: "scalar",   field: "current_stock_qty",    isQty: true,    subField: "skus_with_stock" },
  { key: "capital_atado",  icon: DollarSign,    accent: "text-emerald-700", source: "scalar",   field: "capital_atado",        isMoney: true },
  { key: "dead_stock",     icon: Skull,         accent: "text-rose-700",    source: "scalar",   field: "dead_stock_skus_count",isQty: true,    subField: "dead_stock_days_used" },
];

export default function InventoryKPICards({ data, loading, testId = "inventory-kpis" }) {
  const { t } = useTranslation();

  if (loading || !data) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid={`${testId}-skeleton`}>
        {Array.from({ length: 9 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div
      className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3"
      data-testid={testId}
    >
      {KPI_DEFS.map((def) => {
        const Icon = def.icon;
        let primary = "—";
        let secondary = null;
        let badge = null;

        if (def.source === "movement") {
          const m = data[def.key] || {};
          primary = fmtNum(m.count);
          secondary = `${fmtNum(m[def.qtyKey] || 0)} qty`;
          if (def.key === "devoluciones" && (data.needs_user_review_count || 0) > 0) {
            badge = (
              <Badge
                variant="secondary"
                className="text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-200"
              >
                {t("inventory.needsReview", { defaultValue: "needs review" })}
              </Badge>
            );
          }
        } else {
          const v = data[def.field];
          if (def.isMoney) {
            primary = fmtMoney(v);
          } else {
            primary = fmtNum(v);
          }
          if (def.subField) {
            const sub = data[def.subField];
            if (def.key === "current_stock") {
              secondary = t("inventory.kpi.skusWithStock", { count: sub, defaultValue: `${fmtNum(sub)} SKUs` });
            } else if (def.key === "dead_stock") {
              secondary = t("inventory.kpi.daysWithoutSale", { days: sub, defaultValue: `> ${sub} días sin venta` });
            }
          }
        }

        return (
          <Card
            key={def.key}
            data-testid={`kpi-card-${def.key}`}
            className="border-border/60 hover:border-border transition-colors"
          >
            <CardContent className="p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Icon className={`h-3.5 w-3.5 ${def.accent}`} />
                    <span className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium truncate">
                      {t(`inventory.kpi.${def.key}`, { defaultValue: def.key })}
                    </span>
                  </div>
                  <div className="text-xl font-semibold tabular-nums leading-tight">
                    {primary}
                  </div>
                  {secondary && (
                    <div className="text-[11px] text-muted-foreground mt-0.5 tabular-nums truncate">
                      {secondary}
                    </div>
                  )}
                </div>
                {badge && <div className="shrink-0">{badge}</div>}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
